import { type ReactElement } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useUserEndpoints } from '@/hooks/api/useUserEndpoints';
import { EndpointsTable } from './EndpointsTable';

interface UserEndpointsDialogProps {
  organizationId: string;
  userId: string | null;
  userName: string;
  open: boolean;
  onClose: () => void;
}

/**
 * KIR-3035: standard inventory dialog listing every (live) endpoint a user has
 * reported. KIR-3911: rendering is delegated to the shared {@link EndpointsTable}
 * (new column order, IP/MAC line breaks, agent-version badge, no Inventory link),
 * and the backend already filters to endpoints within the org's
 * inventory-visibility window.
 */
export function UserEndpointsDialog({
  organizationId,
  userId,
  userName,
  open,
  onClose,
}: UserEndpointsDialogProps): ReactElement {
  // applyStaleFilter=true: the dialog hides endpoints past the visibility window.
  const { data, isLoading, isError } = useUserEndpoints(
    organizationId,
    userId,
    open,
    true
  );

  return (
    <Dialog open={open} onOpenChange={isOpen => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>
            Endpoints for{' '}
            <span className="font-semibold">{userName || 'Unknown User'}</span>
          </DialogTitle>
        </DialogHeader>

        <EndpointsTable
          endpoints={data?.endpoints ?? []}
          isLoading={isLoading}
          isError={isError}
        />
      </DialogContent>
    </Dialog>
  );
}

UserEndpointsDialog.displayName = 'UserEndpointsDialog';
